import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { 
  ArrowLeftIcon, 
  CreditCardIcon, 
  DocumentTextIcon,
  ClipboardDocumentCheckIcon 
} from '@heroicons/react/24/outline';

const AdminPrintJobDetailsPage = () => {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [luluDetails, setLuluDetails] = useState(null);
  const [loadingLuluDetails, setLoadingLuluDetails] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showFulfillmentForm, setShowFulfillmentForm] = useState(
    searchParams.get('action') === 'fulfill'
  );
  
  // Form states
  const [luluOrderId, setLuluOrderId] = useState('');
  const [luluOrderCost, setLuluOrderCost] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [shippingCarrier, setShippingCarrier] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const fetchPrintJobDetails = async () => {
      try {
        setLoading(true);
        const db = getFirestore();
        
        // Get the print job document from Firestore
        const jobDoc = await getDoc(doc(db, 'printJobs', jobId));
        
        if (!jobDoc.exists()) {
          toast.error('Print job not found');
          navigate('/admin/print-jobs');
          return;
        }
        
        const jobData = { id: jobDoc.id, ...jobDoc.data() };
        setJob(jobData);
        
        // Fetch user details if userId exists
        if (jobData.userId) {
          const userDoc = await getDoc(doc(db, 'users', jobData.userId));
          if (userDoc.exists()) {
            setUserDetails(userDoc.data());
          }
        }
        
        // Fetch Lulu details if available
        if (jobData.printJobId) {
          try {
            setLoadingLuluDetails(true);
            const NETLIFY_FUNCTION_URL = "/.netlify/functions/lulu-print-job-details";
            const res = await fetch(NETLIFY_FUNCTION_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ printJobId: jobData.printJobId })
            });
            
            if (res.ok) {
              const luluData = await res.json();
              
              // Handle "No PrintJob matches" error response
              if (luluData && luluData.detail && luluData.detail === "No PrintJob matches the given query.") {
                console.log(`Print job ${jobData.printJobId} not found in Lulu API`);
                // Set safe placeholder data
                setLuluDetails({
                  status: { name: 'UNKNOWN', description: 'Print job details not found' },
                  line_items: [],
                  shipping_info: {},
                  // Add other properties the UI might expect
                });
              } else {
                setLuluDetails(luluData);
              }
            }
          } catch (error) {
            console.error('Error fetching Lulu details:', error);
          } finally {
            setLoadingLuluDetails(false);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching print job details:', error);
        setLoading(false);
        toast.error('Failed to load print job details');
      }
    };
    
    fetchPrintJobDetails();
  }, [jobId, navigate]);

  const handleFulfillOrder = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    
    try {
      const db = getFirestore();
      const jobRef = doc(db, 'printJobs', jobId);
      
      await updateDoc(jobRef, {
        paymentStatus: 'MANUALLY_SUBMITTED_TO_LULU',
        luluOrderId,
        luluOrderCost: luluOrderCost ? parseFloat(luluOrderCost) * 100 : null, // Store in cents
        trackingNumber,
        trackingUrl,
        shippingCarrier,
        fulfillmentNotes: notes,
        manuallyFulfilledAt: new Date(),
        updatedAt: new Date()
      });
      
      toast.success('Order marked as fulfilled and submitted to Lulu');
      setShowFulfillmentForm(false);
      
      // Refresh page data
      const updatedJobDoc = await getDoc(jobRef);
      setJob({ id: updatedJobDoc.id, ...updatedJobDoc.data() });
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order: ' + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateTracking = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    
    try {
      const db = getFirestore();
      const jobRef = doc(db, 'printJobs', jobId);
      
      await updateDoc(jobRef, {
        paymentStatus: 'SHIPPED',
        trackingNumber,
        trackingUrl,
        shippingCarrier,
        shippedAt: new Date(),
        updatedAt: new Date()
      });
      
      toast.success('Tracking information updated');
      
      // Refresh page data
      const updatedJobDoc = await getDoc(jobRef);
      setJob({ id: updatedJobDoc.id, ...updatedJobDoc.data() });
    } catch (error) {
      console.error('Error updating tracking:', error);
      toast.error('Failed to update tracking: ' + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  // Get the effective status based on payment, shipping, and Lulu API data
  const getEffectiveStatus = (job) => {
    if (!job) return { name: 'UNKNOWN', message: 'No job data available' };
    
    // If we're still loading Lulu details, show loading status
    if (loadingLuluDetails) {
      return {
        name: 'LOADING',
        message: 'LOADING...'
      };
    }
    
    // Create a combined job object with Lulu details if available
    const combinedJob = {
      ...job,
      details: luluDetails
    };
    
    // Check if job is paid through any means in our app
    const isPaidInApp = combinedJob.stripePaid || 
                   combinedJob.paymentStatus === 'paid' || 
                   combinedJob.paymentStatus === 'PAID_AWAITING_FULFILLMENT' || 
                   combinedJob.customerPaymentStatus === 'paid' ||
                   combinedJob.details?.stripePaymentStatus === 'PAID';
    
    // Check if the print job is paid/in production in Lulu
    // Common Lulu production statuses: CREATED, PAYMENT_RECEIVED, IN_PRODUCTION, etc.
    const isPaidInLulu = combinedJob.details?.status?.name && 
                      ['CREATED', 'PAYMENT_RECEIVED', 'IN_PRODUCTION', 'MANUFACTURING'].includes(
                        combinedJob.details.status.name.toUpperCase());
                        
    // Check if the job is in PRODUCTION_DELAYED status specifically
    const isProductionDelayed = combinedJob.details?.status?.name && 
                            combinedJob.details.status.name.toUpperCase() === 'PRODUCTION_DELAYED';
    
    // Check if the print job has been shipped based on status
    // Only mark as shipped if the status explicitly indicates shipping
    const isShipped = combinedJob.details?.status?.name === 'SHIPPED' || 
                      combinedJob.paymentStatus === 'SHIPPED' || 
                      combinedJob.status === 'SHIPPED';
    
    // If job is both paid and shipped, show it as completed
    if ((isPaidInApp || isPaidInLulu) && isShipped) {
      return {
        name: 'SHIPPED',
        message: 'ORDER COMPLETED - YOUR PACKAGE HAS BEEN SHIPPED'
      };
    }
    
    // If paid in our app and the status is PRODUCTION_DELAYED in Lulu
    if (isPaidInApp && isProductionDelayed) {
      return {
        name: 'PAID',
        message: 'PAID - PRINT PRODUCTION DELAYED - AWAITING PRINTER'
      };
    }
    
    // If paid in our app and also in Lulu (in production)
    if (isPaidInApp && isPaidInLulu) {
      return {
        name: 'PAID',
        message: 'PAID - ORDER SUBMITTED TO PRINTER - IN PRODUCTION'
      };
    }
    
    // If we have a payment indication in our app but not yet processed in Lulu
    if (isPaidInApp) {
      return {
        name: 'PAID',
        message: 'PAYMENT RECEIVED - PREPARING YOUR ORDER'
      };
    }
    
    // For shipped jobs
    if (isShipped) {
      return {
        name: 'SHIPPED',
        message: 'ORDER COMPLETED - YOUR PACKAGE HAS BEEN SHIPPED'
      };
    }
    
    // Otherwise use the status from the database as is, defaulting to UNPAID
    if (combinedJob.status === 'created' || !combinedJob.status) {
      return {
        name: 'UNPAID',
        message: 'Print job was accepted and needs to be paid'
      };
    }
    
    return {
      name: combinedJob.paymentStatus || combinedJob.status || 'UNKNOWN',
      message: ''
    };
  };

  const renderStatusBadge = (status) => {
    // Standardize status for consistent styling
    const normalizedStatus = status?.toUpperCase() || '';
    let className = 'inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium';
    
    switch(normalizedStatus) {
      case 'PAID':
        className += ' bg-blue-50 border border-blue-200 text-blue-800';
        break;
      case 'SHIPPED':
        className += ' bg-green-50 border border-green-200 text-green-800';
        break;
      case 'UNPAID':
        className += ' bg-amber-50 border border-amber-200 text-amber-800';
        break;
      case 'MANUALLY_SUBMITTED_TO_LULU':
        className += ' bg-blue-100 text-blue-800';
        break;
      case 'DELIVERED':
        className += ' bg-indigo-50 border border-indigo-200 text-indigo-800';
        break;
      case 'PRODUCTION_DELAYED':
        className += ' bg-amber-50 border border-amber-200 text-amber-800';
        break;
      default:
        className += ' bg-gray-50 border border-gray-200 text-gray-700';
    }
    
    return (
      <span className={className}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/admin/print-jobs')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Print Jobs
        </button>
        
        <h1 className="text-2xl font-bold text-gray-900">
          Print Job #{job.printJobId || job.id}
        </h1>
        
        {job && (
          <div className="relative p-0.5">
            <div className="rounded-md px-3 py-2 inline-block">
              {/* Display the effective status with both name and message */}
              {renderStatusBadge(getEffectiveStatus(job).name)}
              {getEffectiveStatus(job).message && (
                <div className="text-xs text-gray-600 mt-1 max-w-sm">
                  {getEffectiveStatus(job).message}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Print Job Summary */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Print Job Details
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Created on {job.createdAt ? 
                (typeof job.createdAt.toDate === 'function' ? 
                  job.createdAt.toDate().toLocaleDateString() : 
                  (job.createdAt instanceof Date ? 
                    job.createdAt.toLocaleDateString() : 
                    new Date(job.createdAt).toLocaleDateString()
                  )
                ) : 'Unknown date'}
            </p>
          </div>
          
          {job.printJobId && (
            <button
              onClick={() => {
                const printJobId = job.printJobId || jobId;
                window.open(`https://developers.lulu.com/print-jobs/detail/${printJobId}`, '_blank');
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ClipboardDocumentCheckIcon className="h-5 w-5 mr-2" />
              {job.paymentStatus === 'PAID_AWAITING_FULFILLMENT' ? 'Process Order' : 'View Lulu Job Order'}
            </button>
          )}
          
          {job.paymentStatus === 'MANUALLY_SUBMITTED_TO_LULU' && !job.trackingNumber && (
            <button
              onClick={() => setShowFulfillmentForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Add Tracking Info
            </button>
          )}
        </div>
        
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Customer</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {userDetails ? (
                  <div>
                    <p>{userDetails.displayName || 'No name provided'}</p>
                    <p className="text-gray-500">{userDetails.email}</p>
                  </div>
                ) : (
                  <p>User ID: {job.userId || 'Not available'}</p>
                )}
              </dd>
            </div>
            
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Book</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {job.bookId ? (
                  <Link 
                    to={`/admin/books/${job.bookId}`} 
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    {job.bookTitle || job.bookId}
                  </Link>
                ) : (
                  'Not available'
                )}
              </dd>
            </div>
            
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Payment</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <div className="flex items-center">
                  <CreditCardIcon className="h-5 w-5 mr-2 text-gray-400" />
                  {job.paymentStatus === 'paid' || job.paymentStatus === 'PAID_AWAITING_FULFILLMENT' ? (
                    <span className="text-green-600 font-medium">
                      Paid ${((job.totalAmount || job.amount) / 100).toFixed(2)}
                    </span>
                  ) : (
                    <span>{job.paymentStatus || 'Unknown'}</span>
                  )}
                </div>
                {job.paymentIntentId && (
                  <p className="mt-1 text-gray-500">Payment ID: {job.paymentIntentId}</p>
                )}
                {job.checkoutSessionId && (
                  <p className="mt-1 text-gray-500">Checkout Session: {job.checkoutSessionId}</p>
                )}
              </dd>
            </div>
            
            {job.luluBasePrice && (
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Price Breakdown</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <div className="space-y-1">
                    <p>Lulu Base Cost: ${(job.luluBasePrice / 100).toFixed(2)}</p>
                    <p>Your Markup: ${((job.totalAmount - job.luluBasePrice) / 100).toFixed(2)}</p>
                    <p className="font-medium">Total Paid: ${(job.totalAmount / 100).toFixed(2)}</p>
                  </div>
                </dd>
              </div>
            )}
            
            {/* Lulu Fulfillment Details */}
            {job.paymentStatus === 'MANUALLY_SUBMITTED_TO_LULU' && (
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Lulu Fulfillment</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <div className="space-y-1">
                    {job.luluOrderId && (
                      <p>Lulu Order ID: <span className="font-mono">{job.luluOrderId}</span></p>
                    )}
                    {job.luluOrderCost && (
                      <p>Lulu Order Cost: ${(job.luluOrderCost / 100).toFixed(2)}</p>
                    )}
                    {job.manuallyFulfilledAt && (
                      <p>Fulfilled on: {job.manuallyFulfilledAt.toDate().toLocaleDateString()}</p>
                    )}
                    {job.fulfillmentNotes && (
                      <div>
                        <p className="text-sm font-medium text-gray-500 mt-2">Notes:</p>
                        <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">{job.fulfillmentNotes}</p>
                      </div>
                    )}
                  </div>
                </dd>
              </div>
            )}
            
            {/* Shipping & Tracking */}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Shipping</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <div className="space-y-1">
                  {job.shippingCarrier && (
                    <p>Carrier: {job.shippingCarrier}</p>
                  )}
                  {job.trackingNumber && (
                    <p>
                      Tracking #: <span className="font-mono">{job.trackingNumber}</span>
                      {job.trackingUrl && (
                        <a 
                          href={job.trackingUrl}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="ml-2 text-indigo-600 hover:text-indigo-900"
                        >
                          Track Package
                        </a>
                      )}
                    </p>
                  )}
                  {job.shippedAt && (
                    <p>Shipped on: {job.shippedAt.toDate().toLocaleDateString()}</p>
                  )}
                  
                  {/* Estimated Shipping Dates */}
                  {luluDetails?.estimated_shipping_dates?.dispatch_min && (
                    <p>Est. Ship Date: {new Date(luluDetails.estimated_shipping_dates.dispatch_min).toLocaleDateString()}</p>
                  )}
                  {luluDetails?.estimated_shipping_dates?.dispatch_min && luluDetails?.estimated_shipping_dates?.dispatch_max && (
                    <p>Dispatch Window: {new Date(luluDetails.estimated_shipping_dates.dispatch_min).toLocaleDateString()} - {new Date(luluDetails.estimated_shipping_dates.dispatch_max).toLocaleDateString()}</p>
                  )}
                  {luluDetails?.estimated_shipping_dates?.arrival_min && luluDetails?.estimated_shipping_dates?.arrival_max && (
                    <p>Est. Delivery Window: {new Date(luluDetails.estimated_shipping_dates.arrival_min).toLocaleDateString()} - {new Date(luluDetails.estimated_shipping_dates.arrival_max).toLocaleDateString()}</p>
                  )}
                </div>
              </dd>
            </div>
          </dl>
        </div>
      </div>
      
      {/* Order Fulfillment Form */}
      {showFulfillmentForm && (
        <div className="bg-white shadow sm:rounded-lg p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            {!job.luluOrderId ? 'Process Order in Lulu' : 'Update Tracking Information'}
          </h3>
          
          <form onSubmit={!job.luluOrderId ? handleFulfillOrder : handleUpdateTracking}>
            {!job.luluOrderId && (
              <>
                <div className="mb-4">
                  <label htmlFor="luluOrderId" className="block text-sm font-medium text-gray-700">
                    Lulu Order ID
                  </label>
                  <input
                    type="text"
                    id="luluOrderId"
                    value={luluOrderId}
                    onChange={(e) => setLuluOrderId(e.target.value)}
                    className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="luluOrderCost" className="block text-sm font-medium text-gray-700">
                    Lulu Order Cost (USD)
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      id="luluOrderCost"
                      value={luluOrderCost}
                      onChange={(e) => setLuluOrderCost(e.target.value)}
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </>
            )}
            
            <div className="mb-4">
              <label htmlFor="trackingNumber" className="block text-sm font-medium text-gray-700">
                Tracking Number
              </label>
              <input
                type="text"
                id="trackingNumber"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="trackingUrl" className="block text-sm font-medium text-gray-700">
                Tracking URL
              </label>
              <input
                type="url"
                id="trackingUrl"
                value={trackingUrl}
                onChange={(e) => setTrackingUrl(e.target.value)}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="shippingCarrier" className="block text-sm font-medium text-gray-700">
                Shipping Carrier
              </label>
              <input
                type="text"
                id="shippingCarrier"
                value={shippingCarrier}
                onChange={(e) => setShippingCarrier(e.target.value)}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                placeholder="e.g., USPS, UPS, FedEx"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                Notes
              </label>
              <textarea
                id="notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowFulfillmentForm(false)}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={isUpdating}
              >
                {isUpdating ? 'Saving...' : !job.luluOrderId ? 'Mark as Fulfilled' : 'Update Tracking'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminPrintJobDetailsPage;
